import { Controller } from "@hotwired/stimulus"

import {
  collatorFor,
  groupEntries,
  NO_VALUE_KEY,
  valueKeyFor,
  DEFAULT_NO_VALUE_LABEL,
} from "../../../lib/work/grouping"
import { rollup } from "../../../lib/work/rollups"
import { announce, ensureAnnouncer, interpolate } from "../../../lib/work/announce"

// ui--work-grid-groups — the editable grid's group-by + rollup layer (W6
// Track B). Stacks on the grid surface root beside the engine
// (`data-controller="ui--work-editable-table ui--work-drag
// ui--work-grid-groups ui--work-view-state"`) and renders group sections
// around the ENGINE's rows: one header row (label + count + collapse chevron)
// and one rollup row per section.
//
// PARALLEL-BUILD SEAM (W5 reconciliation — read before merging): grouping is
// client-side over the W5 engine's loaded rows, consumed through the W5 spec's
// DECLARED API (rowIds(), getCell(), setCell()). Two integration points are
// NOT in the W5 spec and are shimmed here, each marked W5-RECONCILIATION
// below:
//   1. row DOM lookup — the controller prefers an engine `rowElement(rowId)`
//      method and falls back to `[data-row-id]` inside the rows host;
//   2. engine-less fallback — without a resolvable engine, row ids, cell
//      values and writes come from `data-row-id` / `data-cell-<field>`
//      attributes (the Lookbook preview path until the W5 bundle lands).
// Both funnels live in _rowElement/_cellValue/_setCellValue; swapping them to
// the landed W5 surface is the entire reconciliation.
//
// ── Behavior contract ───────────────────────────────────────────────────────
//   - Group order: option weight, then locale collation; no-value bucket last
//     (lib/work/grouping.js).
//   - Section headers are buttons with aria-expanded; collapse hides the
//     section's rows + rollup row and announces label + count (engine
//     announcer grammar, lib/work/announce.js).
//   - hideEmpty drops empty sections entirely; a regroup that materializes
//     rows restores them.
//   - Rollups: sum/avg over the loaded rows' numeric values only — NEVER
//     0-filled (null renders an em-dash). `rollups: false` disables the
//     engine rollups so paginating hosts can supply pre-computed group rows.
//   - Drag-through groups: a cross-group drop (W1 kernel, group:* container
//     ids) emits the SAME event as an inline edit — the W5-D4
//     `ui--work:cell-commit {row_id, field_key, value, prev_value}` payload,
//     one PER moved row — and regroups once, optimistically. No separate drop
//     event exists for grouping.
//   - A cell-commit on the group field from ANY source (inline edit, host
//     glue) regroups optimistically the same way.
//
// Events (bubbling, from the surface root): ui--work:cell-commit (the W5
// payload), ui--work:group-by-change {field_key}, ui--work:group-collapse
// {group_key, collapsed} (the timeline grammar's {group_id, collapsed} shape,
// grid-scoped).

const GROUP_CONTAINER_PREFIX = "group:"
const CELL_COMMIT_EVENT = "ui--work:cell-commit"
const GROUP_BY_EVENT = "ui--work:group-by-change"
const GROUP_COLLAPSE_EVENT = "ui--work:group-collapse"
const EM_DASH = "—"

// Live-region copy defaults (host-overridable via the announcements value —
// the ERB hydrates the kit-locale tree over these; a missing key falls back).
const DEFAULT_ANNOUNCEMENTS = {
  collapsed: "{label} collapsed — {count} rows hidden",
  expanded: "{label} expanded — {count} rows shown",
}

export default class extends Controller {
  static targets = ["rowsHost", "headerTemplate", "rollupTemplate", "groupFieldOption"]

  static values = {
    // The group field key ("" = no grouping) — SSR echo of the view state.
    groupField: { type: String, default: "" },
    // Persisted collapse map {"<group key>": true} (JSON).
    collapseState: { type: String, default: "{}" },
    hideEmpty: { type: Boolean, default: false },
    // Engine rollups over loaded rows; false lets a paginating host supply
    // pre-computed group rows instead.
    rollups: { type: Boolean, default: true },
    // "sum" | "avg".
    rollupFn: { type: String, default: "sum" },
    // Rollup columns in display order: [{key, numeric}] (JSON). At merge the
    // surface renders this from the schema; numeric columns roll up, the rest
    // stay em-dash.
    columns: { type: String, default: "[]" },
    // {field_key: text-color class} per column — Ruby composes the classes
    // through Ui::SemanticIntents so tokens stay server-side (JSON).
    rollupClasses: { type: String, default: "{}" },
    // Whether the group field is editable — gates drag-through groups (the
    // schema-derived flag the surface renders).
    groupDraggable: { type: Boolean, default: true },
    // {field_key: [{key, label, weight}]} (JSON) — the groupable fields'
    // option defs, SSR-rendered from the schema so weights/labels stay
    // server-side.
    fieldOptions: { type: String, default: "{}" },
    // Host-overridable announcement copy (JSON map key → {token} template).
    announcements: { type: String, default: "{}" },
    // The no-value bucket's label — SSR-hydrated from
    // work.editable_table.groups.no_value (grouping.js's default is English).
    noValueLabel: { type: String, default: "" },
  }

  connect() {
    // Idempotent connect (D1): full teardown, then rebuild — a Turbo cache
    // restore must not stack sections or listeners.
    this._teardown()
    this._groupField = this.groupFieldValue
    this._collapsed = this._scanJson(this.collapseStateValue) || {}
    this._hideEmpty = this.hideEmptyValue
    this._columns = this._scanJson(this.columnsValue) || []
    this._rollupClasses = this._scanJson(this.rollupClassesValue) || {}
    this._announcements = this._scanJson(this.announcementsValue) || {}
    this._fieldOptions = this._scanJson(this.fieldOptionsValue) || {}
    this._noValueLabel = this.noValueLabelValue || DEFAULT_NO_VALUE_LABEL
    this._announcer = ensureAnnouncer(this.element)

    // One retry covers a slow Stimulus scan for a companion engine/view-state
    // controller (the flow boot pattern); the regroup itself is idempotent.
    this._onCellCommit = (event) => this._handleCellCommit(event)
    this._onDrop = (event) => this._handleKernelDrop(event)
    this.element.addEventListener(CELL_COMMIT_EVENT, this._onCellCommit)
    this.element.addEventListener("ui--work:drop", this._onDrop)
    this._retryTimer = setTimeout(() => this.regroup(), 500)
    this.regroup()
  }

  disconnect() {
    this._teardown()
  }

  // -- Public data API ---------------------------------------------------------

  // Full group-segment apply (the view-state controller's boot/select/revert
  // paths land here). Replaces the working group config and rebuilds.
  applyGroupState(groupState) {
    const source = groupState && typeof groupState === "object" ? groupState : {}
    this._groupField = typeof source.fieldKey === "string" ? source.fieldKey : ""
    this._collapsed =
      source.collapsed && typeof source.collapsed === "object" ? { ...source.collapsed } : {}
    this._hideEmpty = source.hideEmpty === true
    this.regroup()
  }

  // View bar "Group by" action: the menu item carries data-group-field
  // ("" = no grouping). Clears the stale collapse map — a new grouping starts
  // expanded — and reports the change to the host.
  setGroupField(event) {
    const item = event.currentTarget
    const fieldKey = item ? (item.dataset.groupField || "") : ""
    if (fieldKey === this._groupField) return
    this._groupField = fieldKey
    this._collapsed = {}
    this.patchViewState({ fieldKey: fieldKey === "" ? null : fieldKey, collapsed: {} })
    this._refreshGroupFieldOptions()
    this.regroup()
    this.element.dispatchEvent(
      new CustomEvent(GROUP_BY_EVENT, {
        detail: { field_key: fieldKey === "" ? null : fieldKey },
        bubbles: true,
      }),
    )
  }

  // Section header toggle (the header button's click — native button
  // activation covers Enter and Space).
  toggleGroup(event) {
    const header = event.currentTarget.closest("[data-work-group-section]")
    if (!header) return
    const key = header.dataset.groupKey
    // dataset values are STRINGS — compare against "true", never !value.
    const collapsed = header.dataset.collapsed !== "true"
    header.dataset.collapsed = collapsed ? "true" : "false"
    if (collapsed) this._collapsed[key] = true
    else delete this._collapsed[key]
    this.patchViewState({ collapsed: { ...this._collapsed } })
    this._applyCollapsed(header, collapsed)
    this.element.dispatchEvent(
      new CustomEvent(GROUP_COLLAPSE_EVENT, { detail: { group_key: key, collapsed }, bubbles: true }),
    )
    const group = this._lastGroups.find((entry) => entry.key === key)
    this._announce(collapsed ? "collapsed" : "expanded", {
      label: group ? group.label : key,
      count: group ? group.rowIds.length : 0,
    })
  }

  // Rebuild every section from the current rows + group config (idempotent —
  // every mutation path funnels through here).
  regroup() {
    if (!this.hasRowsHostTarget) return
    const host = this.rowsHostTarget
    const entries = this._rowEntries()
    const field = { key: this._groupField, type: "text", options: this._groupOptions() }
    this._lastGroups = groupEntries(entries, field, {
      collator: collatorFor(document.documentElement?.lang),
      hideEmpty: this._hideEmpty,
      noValueLabel: this._noValueLabel,
    })
    this._renderSections(host)
  }

  // -- Internals: mutation paths ----------------------------------------------

  _teardown() {
    if (this._retryTimer) {
      clearTimeout(this._retryTimer)
      this._retryTimer = null
    }
    if (this._onCellCommit) this.element.removeEventListener(CELL_COMMIT_EVENT, this._onCellCommit)
    if (this._onDrop) this.element.removeEventListener("ui--work:drop", this._onDrop)
    this._onCellCommit = null
    this._onDrop = null
  }

  // Any cell-commit on the group field regroups optimistically — inline edits,
  // drag-through drops and host glue all land here, so there is exactly one
  // re-sectioning path. A drag-through gesture defers its regroup (see
  // _handleKernelDrop) so a multi-row gesture still coalesces into one.
  _handleCellCommit(event) {
    const detail = event.detail || {}
    if (this._deferRegroup) return
    if (this._groupField === "" || detail.field_key !== this._groupField) return
    this.regroup()
  }

  // Drag-through groups: the W1 kernel has already MOVED the row DOM into the
  // target section when `ui--work:drop` arrives (kernel _applySlot moves before
  // emitting). This handler converts the move into the SAME shape as an inline
  // edit — set the group value and emit the W5-D4 `ui--work:cell-commit` PER
  // ROW (the payload is singular, so a multi-row cross-group drop emits one
  // commit per moved row). The ONE deferred thing is the REGROUP: each
  // commit's own re-sectioning stays suppressed until every row has applied.
  _handleKernelDrop(event) {
    const detail = event.detail || {}
    if (!this.groupDraggableValue || this._groupField === "") return
    const container = String(detail.container || "")
    const from = String(detail.from_container || "")
    if (!container.startsWith(GROUP_CONTAINER_PREFIX)) return
    if (!from.startsWith(GROUP_CONTAINER_PREFIX)) return
    if (container === from) return // same-section reorder: no rank contract in grid v1
    const movedIds = Array.isArray(detail.moved_ids) ? detail.moved_ids : []
    if (movedIds.length === 0) return
    const nextKey = container.slice(GROUP_CONTAINER_PREFIX.length)
    const nextValue = this._optionValueFor(nextKey)

    // One gesture, one re-sectioning: defer the commit-driven regroups until
    // every row of the gesture has applied.
    this._deferRegroup = true
    try {
      for (const rowId of movedIds) {
        const prev = this._cellValue(rowId, this._groupField)
        if (valueKeyFor(prev) === nextKey) continue
        this._setCellValue(rowId, this._groupField, nextValue)
        // The W5-D4 payload, verbatim: {row_id, field_key, value, prev_value}.
        this.element.dispatchEvent(
          new CustomEvent(CELL_COMMIT_EVENT, {
            detail: {
              row_id: rowId,
              field_key: this._groupField,
              value: nextValue,
              prev_value: prev,
            },
            bubbles: true,
          }),
        )
      }
    } finally {
      this._deferRegroup = false
    }
    this.regroup()
  }

  // -- Internals: row access (the two W5-RECONCILIATION funnels) --------------

  _engine() {
    // Re-resolved PER USE — never cached: the co-mounted kernel is the live
    // controller instance on this element, and a Turbo morph that cycles the
    // root re-instantiates every controller on it. A cached engine is a dead
    // instance whose reads answer stale rows and whose writes land nowhere
    // (silently — nothing throws). getControllerForElementAndIdentifier is a
    // registry lookup, cheap enough to pay on every cell access, and matches
    // the kit's dominant kernel-lookup pattern (issue_transitions,
    // cell_editor, navigator resolve per use; the view-state lookup below
    // does the same).
    try {
      const engine = this.application.getControllerForElementAndIdentifier(
        this.element,
        "ui--work-editable-table",
      )
      // The declared W5 API surface this layer consumes.
      return engine && typeof engine.getCell === "function" && typeof engine.rowIds === "function"
        ? engine
        : null
    } catch {
      return null
    }
  }

  // W5-RECONCILIATION 1: the W5 spec declares rowIds()/getCell()/setCell() but
  // NOT a row-element accessor. Prefer one if the landed engine grows it;
  // otherwise fall back to the rows host's `data-row-id` nodes.
  _rowElement(rowId) {
    const engine = this._engine()
    if (engine && typeof engine.rowElement === "function") {
      const el = engine.rowElement(rowId)
      if (el) return el
    }
    return this.rowsHostTarget.querySelector(`[data-row-id="${CSS.escape(String(rowId))}"]`)
  }

  _rowEntries() {
    const engine = this._engine()
    let ids
    if (engine && typeof engine.rowIds === "function") {
      ids = engine.rowIds() || []
    } else {
      ids = [...this.rowsHostTarget.querySelectorAll("[data-row-id]")].map(
        (el) => el.getAttribute("data-row-id"),
      )
    }
    const key = this._groupField
    if (key === "") return ids.map((id) => ({ id, value: null }))
    return ids.map((id) => ({ id, value: this._cellValue(id, key) }))
  }

  // W5-RECONCILIATION 2 (engine-less fallback): reads the canonical cell value
  // from `data-cell-<field>` when no engine is mounted (Lookbook previews
  // until the W5 bundle lands). The engine path uses getCell().
  _cellValue(rowId, fieldKey) {
    const engine = this._engine()
    if (engine) return engine.getCell(rowId, fieldKey)
    const el = this._rowElement(rowId)
    if (!el) return null
    const raw = el.getAttribute(`data-cell-${fieldKey}`)
    if (raw === null || raw === "") return null
    return raw
  }

  _setCellValue(rowId, fieldKey, value) {
    const engine = this._engine()
    if (engine) {
      engine.setCell(rowId, fieldKey, value)
      return
    }
    const el = this._rowElement(rowId)
    if (el) el.setAttribute(`data-cell-${fieldKey}`, String(value))
  }

  // -- Internals: section rendering --------------------------------------------

  // Build the section structure inside the rows host. Order of operations is
  // load-bearing: rows are re-homed into the host BEFORE the previous section
  // wrappers are removed (removing a wrapper would otherwise remove its rows),
  // then sections are rebuilt around the rows.
  _renderSections(host) {
    const rows = [...host.querySelectorAll("[data-row-id]")]
    for (const row of rows) host.appendChild(row) // re-home (moves, not clones)
    this._sectionWrappers().forEach((section) => section.remove())
    if (this._groupField === "") return

    const byId = new Map(rows.map((row) => [row.getAttribute("data-row-id"), row]))
    const groups = this._lastGroups || []

    for (const group of groups) {
      const section = document.createElement("div")
      section.setAttribute("role", "rowgroup")
      section.setAttribute("data-work-group-section", "")
      section.setAttribute("data-group-key", group.key)
      // Kernel runtime wiring: the W1 drag kernel watches for containers as
      // they appear (containerTargetConnected), so dynamic sections become
      // drop targets without re-scanning the surface.
      section.setAttribute("data-ui--work-drag-target", "container")
      section.setAttribute("data-container-id", `${GROUP_CONTAINER_PREFIX}${group.key}`)
      const collapsed = this._collapsed[group.key] === true
      section.setAttribute("data-collapsed", collapsed ? "true" : "false")

      if (this.hasHeaderTemplateTarget) {
        const header = this.headerTemplateTarget.content.firstElementChild.cloneNode(true)
        const label = header.querySelector("[data-group-label]")
        const count = header.querySelector("[data-group-count]")
        if (label) label.textContent = group.label
        if (count) count.textContent = String(group.rowIds.length)
        section.appendChild(header)
      }

      for (const rowId of group.rowIds) {
        const row = byId.get(rowId)
        if (row) section.appendChild(row)
      }

      if (this.hasRollupTemplateTarget && this.rollupsValue) {
        section.appendChild(this._buildRollupRow(group))
      }
      this._applyCollapsed(section, collapsed)
      host.appendChild(section)
    }
  }

  _sectionWrappers() {
    return [...this.rowsHostTarget.querySelectorAll("[data-work-group-section]")]
  }

  // One rollup row: label cell + one shell cell per column. Numeric columns
  // roll up over the group's loaded rows; every other cell — and a numeric
  // column with no numeric values — is the em-dash, never a zero-filled cell.
  _buildRollupRow(group) {
    const rollupRow = this.rollupTemplateTarget.content.firstElementChild.cloneNode(true)
    rollupRow.setAttribute("data-work-group-rollup", "")
    rollupRow.setAttribute("data-group-key", group.key)
    const count = rollupRow.querySelector("[data-rollup-count]")
    if (count) count.textContent = String(group.rowIds.length)

    const proto = rollupRow.querySelector("[data-rollup-cell]")
    const labelText = rollupRow.querySelector("[data-rollup-label-text]")
    if (labelText) labelText.textContent = group.label
    if (proto) {
      for (const column of this._columns) {
        const cell = proto.cloneNode(true)
        cell.removeAttribute("data-rollup-cell")
        cell.setAttribute("data-rollup-key", column.key)
        const classes = this._rollupClasses[column.key]
        if (classes) cell.setAttribute("class", `${cell.getAttribute("class") || ""} ${classes}`.trim())
        cell.textContent = this._rollupDisplay(group.rowIds, column)
        proto.insertAdjacentElement("beforebegin", cell)
      }
      proto.remove()
    }
    return rollupRow
  }

  _rollupDisplay(rowIds, column) {
    if (!column.numeric) return EM_DASH
    const values = rowIds.map((rowId) => {
      const raw = this._cellValue(rowId, column.key)
      if (typeof raw === "number") return Number.isFinite(raw) ? raw : null
      // The Number() coercion below exists for the DOM-FALLBACK path only:
      // without an engine, cell values arrive as data-cell-<field> STRINGS.
      // (rollups.js deliberately keeps numeric strings AS strings — see its
      // numeric-strings-are-not-numbers guard.) The engine path hands back
      // real numbers through getCell().
      if (typeof raw !== "string" || raw.trim() === "") return null
      const parsed = Number(raw)
      return Number.isFinite(parsed) ? parsed : null
    })
    const value = rollup(values, this.rollupFnValue)
    return value === null ? EM_DASH : String(value)
  }

  _setHidden(el, hidden) {
    if (hidden) el.setAttribute("hidden", "")
    else el.removeAttribute("hidden")
  }

  // Collapse = hide the section's rows + rollup row (the `hidden` attribute —
  // no stylesheet changes needed); the header row stays and reports
  // aria-expanded on its toggle button, and the chevron the
  // GroupHeaderComponent shell carries rotates via the `-rotate-90` utility
  // (`transition-transform` is baked into the shell's chevron classes).
  _applyCollapsed(section, collapsed) {
    const headerRow = section.querySelector("[data-work-group-header]")
    if (headerRow) {
      const button = headerRow.matches("button") ? headerRow : headerRow.querySelector("button")
      if (button) button.setAttribute("aria-expanded", collapsed ? "false" : "true")
    }
    const chevron = section.querySelector("[data-group-chevron]")
    if (chevron) chevron.classList.toggle("-rotate-90", collapsed)
    section
      .querySelectorAll(":scope > [data-row-id], :scope > [data-work-group-rollup]")
      .forEach((el) => this._setHidden(el, collapsed))
  }

  _announce(key, tokens) {
    const template = this._announcements[key] || DEFAULT_ANNOUNCEMENTS[key]
    if (!template) return
    announce(this._announcer, template, tokens)
  }

  // -- Internals: view-state + selector sync -----------------------------------

  patchViewState(partial) {
    try {
      const viewState = this.application.getControllerForElementAndIdentifier(
        this.element,
        "ui--work-view-state",
      )
      if (viewState && typeof viewState.patchState === "function") {
        viewState.patchState("group", partial)
      }
    } catch {
      // No view-state controller beside us (engine-less previews) — the group
      // segment simply has no writer; the grid still groups.
    }
  }

  _refreshGroupFieldOptions() {
    this.groupFieldOptionTargets.forEach((option) => {
      const active = (option.dataset.groupField || "") === this._groupField
      option.setAttribute("aria-checked", active ? "true" : "false")
      if (active) option.setAttribute("aria-current", "true")
      else option.removeAttribute("aria-current")
    })
  }

  _groupOptions() {
    const options = this._fieldOptions[this._groupField]
    return Array.isArray(options) ? options : []
  }

  // The value written back through setCell for a group key: the option key
  // for selects, real booleans for checkbox keys, null for the no-value
  // bucket.
  _optionValueFor(key) {
    if (key === NO_VALUE_KEY) return null
    if (key === "true") return true
    if (key === "false") return false
    return key
  }

  _scanJson(raw) {
    if (!raw || raw === "") return null
    try {
      return JSON.parse(raw)
    } catch {
      return null
    }
  }
}
